// src/App.js
import React, { useState, useEffect, useCallback, useRef, Suspense } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { auth, db } from "./firebase.js";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { collection, getDocs, setDoc, doc, deleteDoc } from "firebase/firestore";
import backgroundImage from './assets/nmr_main.jpg';
import OwnersPage from "./OwnersPage.js";
import InvoicesPage from "./InvoicesPage.js";
import ContractorsPage from "./ContractorsPage.js";
import CalendarPage from "./CalendarPage.js";
import BoardMembersPage from "./BoardMembersPage.js";
import MeetingMinutesPage from "./MeetingMinutesPage.js";
import SharedFilesPage from "./SharedFilesPage.js";
import HOADocsPage from "./HOADocsPage.js";
import AdminInvoicesPage from "./AdminInvoicesPage.js";
import AdminFinancePage from "./AdminFinancePage.js";
import AdminCompliancePage from "./AdminCompliancePage.js";
import ComplianceCalendarPage from "./ComplianceCalendarPage.js";
import RentalsPage from "./RentalsPage.js";
import CommunityPicsPage from "./CommunityPicsPage.js";
import MediaDetailPage from "./MediaDetailPage.js";
import AdminUsersPage from "./AdminUsersPage.js";
import MessageBoardPage from "./MessageBoardPage.js";
import VideoPage from "./VideoPage.js";
import UserPreferencesPage from "./UserPreferencesPage.js";
import NeighborhoodMap from "./NeighborhoodMap.js";
import FlightRadarPage from "./FlightRadarPage.js";
import PlantTreeIdentification from "./PlantTreeIdentification.js";
import AnimalIdentification from "./AnimalIdentification.js";
import MineralIdentification from "./MineralIdentification.js";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler } from "chart.js";
import useIsMobile from './useIsMobile.js';
import Header from './header.js';
import WeatherAlertBanner from './WeatherAlertBanner.js';
import LocalRiverInfo from "./LocalRiverInfo.js";
import SeismicMonitoring from "./SeismicMonitoring.js";
import RoadConditionsPage from "./RoadConditionsPage.js";
import WellMonitoringPage from "./WellMonitoringPage.js";
import GateAccessPage from "./GateAccessPage.js";
import MailboxPage from "./MailboxPage.js";
import AdminMailboxPage from "./AdminMailboxPage.js";
import VehicleLogPage from "./VehicleLogPage.js";
import ViolationsPage from "./ViolationsPage.js";
import ArcReviewPage from "./ArcReviewPage.js";
import DashboardQuickLinks from "./DashboardQuickLinks.js";
import PrivacyPolicyPage from "./PrivacyPolicyPage.js";
import TermsAndConditionsPage from "./TermsAndConditionsPage.js";
import MarketingLandingPage from "./MarketingLandingPage.js";
import HoaSalesPage from "./HoaSalesPage.js";
import HoaBoardOnePager from "./HoaBoardOnePager.js";
import DemoBanner from "./DemoBanner.js";
import HelpPage from "./HelpPage.js";
import NannyAssistant from "./NannyAssistant.js";
import AdminPortalPage from "./AdminPortalPage.js";
import DeveloperGuidePage from "./DeveloperGuidePage.js";
import CommunityServicesPage from "./CommunityServicesPage.js";
import MarketplacePage from "./MarketplacePage.js";
import EnvironmentalPage from "./EnvironmentalPage.js";
import AdminSmsPage from "./AdminSmsPage.js";
import { isDemoBuild, DEMO_SITE_URL } from "./siteConfig.js";
import { fetchLocalNews } from "./localNews.js";
import { bootstrapDemoMode } from "./demo/bootstrap.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchInstallLinks } from "./appInstallLinks.js";
import PollsPage from "./PollsPage.js";
import AdminPollsPage from "./AdminPollsPage.js";
import './App.css';
const StarMapPage = React.lazy(() => import('./StarMapPage.js'));
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);
console.log("React Router imports:", { Router, Routes, Route, Link, useNavigate, Navigate });
console.log("Firebase imports:", { auth, db, signInWithEmailAndPassword, signOut, collection, getDocs });
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) { console.error("Error caught in ErrorBoundary:", error, errorInfo); }
  render() { return this.state.hasError ? <div>Error: {this.state.error.message}</div> : this.props.children; }
}
function LoginWindow({ onClose, isMobile, redirectTo, ringLinkPending, demoMode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);
  const navigate = useNavigate();
  const handleLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setError(null);
      setResetMessage(null);
      onClose();
      navigate(redirectTo || "/dashboard", { replace: true });
    } catch (error) {
      console.error("Login failed:", error);
      setError("Login failed: " + error.message);
      setResetMessage(null);
    }
  };
  const handlePasswordReset = async () => {
    if (!email) {
      setError("Please enter your email address to reset your password.");
      setResetMessage(null);
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetMessage("Password reset email sent! Check your inbox.");
      setError(null);
    } catch (error) {
      console.error("Password reset failed:", error);
      setError("Failed to send reset email: " + error.message);
      setResetMessage(null);
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };
  return createPortal(
    <div className="modal-overlay login-modal-overlay" style={{ display: 'flex' }} onClick={onClose}>
      <div
        className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="owner-login-title"
      >
        <h2 id="owner-login-title" className="modal-title">Owner's Login</h2>
        {demoMode && (
          <p className="modal-ring-hint">
            Demo owner: <strong>owner@pineridge.demo</strong> (any password). Admin: <strong>admin@pineridge.demo</strong>.
          </p>
        )}
        {ringLinkPending && (
          <p className="modal-ring-hint">
            Log in with your admin account to finish connecting Ring to the community mailbox.
          </p>
        )}
        {error && <p className="modal-error">{error}</p>}
        {resetMessage && <p className="modal-success">{resetMessage}</p>}
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="modal-input"
            autoFocus
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="modal-input"
            autoComplete="current-password"
          />
          <div className="modal-button-group">
            <button
              type="submit"
              className="modal-button modal-button-primary"
            >
              Login
            </button>
            <button
              type="button"
              onClick={onClose}
              className="modal-button modal-button-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
        <button
          type="button"
          onClick={handlePasswordReset}
          className="modal-button modal-button-link"
        >
          Forgot Password?
        </button>
      </div>
    </div>,
    document.body,
  );
}

function RingLinkLoginHelper({ onOpenLogin, setPostLoginPath }) {
  const location = useLocation();
  const handledSearch = useRef("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("ring") !== "link" || !params.get("nonce") || !params.get("time")) {
      return;
    }
    if (handledSearch.current === location.search) {
      return;
    }
    handledSearch.current = location.search;
    setPostLoginPath(`/admin-mailbox?${params.toString()}`);
    onOpenLogin();
  }, [location.search, onOpenLogin, setPostLoginPath]);

  return null;
}

function EmergencyMessageDialogWrapper({ isMobile, user }) {
  const navigate = useNavigate();
  const [emergencyMessage, setEmergencyMessage] = useState("");
  useEffect(() => {
    const fetchEmergencyMessage = async () => {
      if (!user) {
        console.log("Skipping fetchEmergencyMessage: User not authenticated");
        return;
      }
      try {
        const snapshot = await getDocs(collection(db, "emergency_alerts"));
        if (!snapshot.empty) {
          const alertDoc = snapshot.docs[0];
          setEmergencyMessage(alertDoc.data().message || "");
        } else {
          setEmergencyMessage("");
        }
      } catch (error) {
        console.error("Error fetching emergency message:", error);
      }
    };
    fetchEmergencyMessage();
  }, [user]);
  return (
    <EmergencyMessageDialog
      isOpen={true}
      onClose={() => navigate("/dashboard")}
      currentMessage={emergencyMessage}
      onSave={async (message) => {
        try {
          await setDoc(doc(db, "emergency_alerts", "active_alert"), {
            message,
            createdAt: new Date(),
            createdBy: user.uid,
          });
          setEmergencyMessage(message);
        } catch (error) {
          console.error("Error saving emergency message:", error);
        }
      }}
      onDelete={async () => {
        try {
          await deleteDoc(doc(db, "emergency_alerts", "active_alert"));
          setEmergencyMessage("");
        } catch (error) {
          console.error("Error deleting emergency message:", error);
        }
      }}
      isMobile={isMobile}
    />
  );
}
function EmergencyMessageDialog({ isOpen, onClose, currentMessage, onSave, onDelete, isMobile }) {
  const [message, setMessage] = useState(currentMessage || "");
  useEffect(() => {
    setMessage(currentMessage || "");
  }, [currentMessage, isOpen]);
  const handleSave = () => {
    onSave(message);
    onClose();
  };
  const handleDelete = () => {
    onDelete();
    onClose();
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">Manage Emergency Message</h2>
        <form onSubmit={handleSubmit}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter emergency message"
            className="modal-textarea"
            autoFocus
          />
          <div className="modal-button-group">
            <button type="submit" className="modal-button modal-button-primary">Save</button>
            <button type="button" onClick={handleDelete} className="modal-button modal-button-danger">Delete</button>
            <button type="button" onClick={onClose} className="modal-button modal-button-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
function MainPage({ onLoginClick, isPublic, user, isAdmin, weatherData, historicalWeatherData, events, eventsError, isMobile, ringLinkPending }) {
  console.log("Rendering MainPage component");
  const [emergencyMessage, setEmergencyMessage] = useState("");
  const [localNews, setLocalNews] = useState([]);
  const [localNewsLoading, setLocalNewsLoading] = useState(true);
  const [localNewsError, setLocalNewsError] = useState(null);
  const [installLinks, setInstallLinks] = useState({ ios: "", android: "" });
  useEffect(() => {
    const fetchEmergencyMessage = async () => {
      if (!user) {
        console.log("Skipping fetchEmergencyMessage: User not authenticated");
        return;
      }
      try {
        const snapshot = await getDocs(collection(db, "emergency_alerts"));
        if (!snapshot.empty) {
          const alertDoc = snapshot.docs[0];
          setEmergencyMessage(alertDoc.data().message || "");
        } else {
          setEmergencyMessage("");
        }
      } catch (error) {
        console.error("Error fetching emergency message:", error);
      }
    };
    fetchEmergencyMessage();
  }, [user]);
  useEffect(() => {
    if (!user) {
      setLocalNewsLoading(false);
      setLocalNews([]);
      setLocalNewsError(null);
      return undefined;
    }

    let cancelled = false;

    const loadLocalNews = async () => {
      setLocalNewsLoading(true);
      setLocalNewsError(null);
      try {
        const { news } = await fetchLocalNews({ user });
        if (!cancelled) {
          setLocalNews(news);
          if (news.length === 0) {
            setLocalNewsError('Headlines are temporarily unavailable. Try again in a few minutes.');
          }
        }
      } catch (error) {
        console.error('Error fetching local news:', error);
        if (!cancelled) {
          setLocalNews([]);
          setLocalNewsError('Unable to load local news right now.');
        }
      } finally {
        if (!cancelled) {
          setLocalNewsLoading(false);
        }
      }
    };

    loadLocalNews();
    return () => {
      cancelled = true;
    };
  }, [user]);
  useEffect(() => {
    if (!isPublic) return;
    let mounted = true;
    fetchInstallLinks().then((links) => {
      if (mounted) setInstallLinks(links);
    });
    return () => {
      mounted = false;
    };
  }, [isPublic]);
  const normalizeUrl = (url) => {
    if (typeof url !== 'string' || url.trim() === '') {
      return '#';
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return 'https://' + url;
  };
  const hasHistoricalPressure = historicalWeatherData?.some(d => {
    const p = d.imperial?.pressure ?? d.imperial?.pressureAvg;
    return p != null;
  });

  const currentPressureValue = weatherData?.rawData?.imperial?.pressure
    ? Number(weatherData.rawData.imperial.pressure)
    : null;

  const chartData = historicalWeatherData ? {
    labels: historicalWeatherData.map(data => new Date(data.obsTimeLocal).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
    datasets: [
      {
        label: 'Temperature (°F)',
        data: historicalWeatherData.map(data => {
          const t = data.imperial?.tempAvg;
          return t != null ? Number(t) : null;
        }),
        borderColor: '#0ea5e9',
        backgroundColor: 'rgba(14, 165, 233, 0.06)',
        borderWidth: 2.5,
        tension: 0.35,
        fill: true,
        spanGaps: true,
        yAxisID: 'yTemp',
      },
      {
        label: 'Wind Speed (mph)',
        data: historicalWeatherData.map(data => {
          const w = data.imperial?.windspeedAvg;
          return w != null ? Number(w) : null;
        }),
        borderColor: '#f43f5e',
        backgroundColor: 'rgba(244, 63, 94, 0.08)',
        borderWidth: 2,
        tension: 0.35,
        spanGaps: true,
        yAxisID: 'yWind',
      },
      // Use the same pressure value shown in the current readings box as a horizontal reference line
      // (real historical pressure series is not provided by the station's hourly API)
      ...(currentPressureValue ? [{
        label: 'Current Pressure',
        data: historicalWeatherData.map(() => currentPressureValue),
        borderColor: '#8b5cf6',
        borderDash: [5, 4],
        borderWidth: 1.75,
        pointRadius: 0,
        fill: false,
        yAxisID: 'yPressure',
      }] : []),
    ],
  } : null;
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 16,
          font: { size: 13, weight: 500 },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        titleFont: { size: 13, weight: 600 },
        bodyFont: { size: 14 },
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(226, 232, 240, 0.35)', lineWidth: 0.5 },
        ticks: { color: '#64748b', font: { size: 12 } },
      },
      yTemp: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Temp (°F)', color: '#0ea5e9', font: { size: 12, weight: 500 } },
        grid: { color: 'rgba(226, 232, 240, 0.35)', lineWidth: 0.5 },
        ticks: { color: '#64748b', font: { size: 12 } },
      },
      yWind: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Wind (mph)', color: '#f43f5e', font: { size: 12, weight: 500 } },
        grid: { drawOnChartArea: false },
        ticks: { color: '#64748b', font: { size: 12 } },
      },
      // Always show pressure axis when we have the current pressure (from the readings box)
      ...(currentPressureValue && {
        yPressure: {
          type: 'linear',
          position: 'right',
          title: { display: true, text: 'Pressure (inHg)', color: '#8b5cf6', font: { size: 12, weight: 500 } },
          grid: { drawOnChartArea: false },
          min: 29.2,
          max: 30.6,
          ticks: {
            color: '#64748b',
            font: { size: 12 },
            stepSize: 0.2,
            callback: (value) => value.toFixed(1),
          },
        },
      }),
    },
  };
  const currentDate = new Date();
  const thirtyDaysFromNow = new Date(currentDate.getTime() + 30 * 24 * 60 * 60 * 1000);
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.start);
    return eventDate >= currentDate && eventDate <= thirtyDaysFromNow;
  });
  return (
    <div className="main-page-container">
      <main className="main-page-content">
        <div className="content-wrapper">
          {isPublic && (
            <div className="public-landing-card">
              <h2 className="public-landing-title">Welcome</h2>
              {ringLinkPending && (
                <p className="public-landing-ring-notice">
                  Ring connection pending — log in with your admin account to finish linking the community mailbox.
                </p>
              )}
              <p className="public-landing-text">
                Welcome to the Nantahala Mountain Resort owner community portal. Log in to access invoices,
                documents, community features, and local monitoring tools.
              </p>
              <div className="public-landing-actions">
                <button type="button" className="public-landing-login" onClick={onLoginClick}>
                  Owner&apos;s Login
                </button>
                <Link to="/neighborhood-map" className="public-landing-map-link">
                  View Neighborhood Map
                </Link>
              </div>
              <div className="app-install-section">
                <h3 className="app-install-title">Install the Mobile App</h3>
                <p className="app-install-note">Use the same owner login on your phone after installing.</p>
                <div className="app-install-buttons">
                  {installLinks.ios ? (
                    <a
                      href={installLinks.ios}
                      className="app-install-btn app-install-ios"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Install for iPhone
                    </a>
                  ) : null}
                  {installLinks.android ? (
                    <a
                      href={installLinks.android}
                      className="app-install-btn app-install-android"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Install for Android
                    </a>
                  ) : null}
                </div>
                {installLinks.android ? (
                  <p className="app-install-hint">
                    Android installs use Google Play internal testing. Your Google account email must be on the tester list.
                  </p>
                ) : null}
              </div>
              <p className="public-landing-legal">
                <Link to="/privacy" className="public-landing-map-link">
                  Privacy Policy
                </Link>
              </p>
            </div>
          )}
          {!isPublic && (
            <>
              <DashboardQuickLinks user={user} />
              <div className="dashboard-top-sections">
                <div className="dashboard-events">
                  <h2 className="dashboard-section-title">Upcoming Events</h2>
                  {eventsError ? (
                    <p className="text-red-500">{eventsError}</p>
                  ) : upcomingEvents.length > 0 ? (
                    <ul>
                      {upcomingEvents.map((event) => (
                        <li key={event.id}>
                          {event.title} - {event.date}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No upcoming events within the next 30 days</p>
                  )}
                </div>
                <div className="dashboard-news">
                  <h2 className="dashboard-section-title">Local News</h2>
                  {localNewsLoading ? (
                    <div className="dashboard-news-loading" role="status" aria-live="polite">
                      <span className="dashboard-spinner" aria-hidden="true" />
                      <span>Loading local news…</span>
                    </div>
                  ) : localNewsError ? (
                    <p className="dashboard-news-error">{localNewsError}</p>
                  ) : localNews.length > 0 ? (
                    <ul className="dashboard-news-list">
                      {localNews.slice(0, 5).map((item, index) => (
                        <li key={index} className="dashboard-news-item">
                          <a href={normalizeUrl(item.url)} target="_blank" rel="noopener noreferrer" className="dashboard-news-link">
                            {item.title}
                          </a>
                          <div className="dashboard-news-meta">
                            <span className="news-source">{item.source}</span>
                            <span className="news-date">{item.date}</span>
                          </div>
                          <p className="dashboard-news-description">{item.description}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No local news available</p>
                  )}
                </div>
              </div>
              {emergencyMessage && (
                <div className={`emergency-message ${isMobile ? 'emergency-message-mobile' : ''}`}>
                  <h2 className="emergency-title">Emergency Alert</h2>
                  <p>{emergencyMessage}</p>
                </div>
              )}
            </>
          )}
          <div className="flex-grow"></div>
          <div className="dashboard-weather">
            <div className="weather-console-header">
              <div>
                <h2 className="weather-console-title">Onsite Weather Station</h2>
                <p className="weather-console-subtitle">Nantahala Mountain Resort Summit • Live Data</p>
              </div>
              <div className="weather-last-update">
                Updated: {weatherData?.rawData?.obsTimeLocal || 'N/A'}
              </div>
            </div>

            {weatherData ? (
              <div className="weather-console">
                {/* Big Temp */}
                <div className="weather-main">
                  <div className="temp-display">
                    <span className="temp-value">{weatherData.temp || '--'}</span>
                    <span className="temp-unit">°F</span>
                  </div>
                  <div className="temp-feels">
                    {weatherData.rawData && (
                      <>
                        Feels like {weatherData.rawData.imperial.heatIndex || weatherData.rawData.imperial.windChill || weatherData.temp}
                      </>
                    )}
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="weather-metrics">
                  <div className="metric">
                    <div className="metric-label">Humidity</div>
                    <div className="metric-value">{weatherData.humidity || '--'}<span>%</span></div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Wind</div>
                    <div className="metric-value">{weatherData.wind || '--'}</div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Gust</div>
                    <div className="metric-value">{weatherData.rawData?.imperial.windGust || '--'}<span> mph</span></div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Pressure</div>
                    <div className="metric-value">{weatherData.rawData?.imperial.pressure || '--'}<span> inHg</span></div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Rain Rate</div>
                    <div className="metric-value">{weatherData.rawData?.imperial.precipRate || '0'}<span> in/hr</span></div>
                  </div>
                  <div className="metric">
                    <div className="metric-label">Rain Today</div>
                    <div className="metric-value">{weatherData.rawData?.imperial.precipTotal || '0'}<span> in</span></div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center">Weather data unavailable</p>
            )}

            {/* Professional Trends Chart */}
            {historicalWeatherData && historicalWeatherData.length > 0 ? (
              <div className="weather-console-chart">
                <div className="chart-header">
                  <h3>24-Hour Trends</h3>
                </div>
                <div className="weather-chart-wrapper">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            ) : (
              <p className="mt-4 text-center">No historical data available</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
function ProtectedRoute({ children, isMobile, requiresAdmin }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          navigate("/");
          setAuthLoading(false);
        } else {
          const idTokenResult = await user.getIdTokenResult();
          const adminStatus = idTokenResult.claims.isAdmin === true;
          setUser(user);
          setIsAdmin(adminStatus);
          await new Promise(resolve => setTimeout(resolve, 2000));
          setAuthLoading(false);
        }
      } catch (err) {
        console.error("Error in auth state change handler:", err);
        setError(err.message);
        setAuthLoading(false);
      }
    }, (error) => {
      console.error("Auth state change error:", error);
      setError(error.message);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);
  if (authLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!user) return null;
  if (requiresAdmin && !isAdmin) {
    return (
      <div className="docs-page">
        <article className="docs-card">
          <h1>Access denied</h1>
          <p>This page requires administrator privileges.</p>
        </article>
      </div>
    );
  }
  const childrenArray = React.Children.toArray(children);
  if (childrenArray.length === 0) return <div>Error: No children to render</div>;
  return (
    <>
      {childrenArray.map((child, index) => React.cloneElement(child, { user, isAdmin, authLoading, isMobile, key: index }))}
    </>
  );
}
function App() {
  const [showLogin, setShowLogin] = useState(false);
  const [postLoginPath, setPostLoginPath] = useState(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [historicalWeatherData, setHistoricalWeatherData] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isDemoBuild) {
      bootstrapDemoMode((message) => toast.info(message, { autoClose: 4000 }));
    }
  }, []);

  const handleTryDemo = async () => {
    try {
      await signInWithEmailAndPassword(auth, "owner@pineridge.demo", "demo");
      window.location.href = "/dashboard";
    } catch (error) {
      console.error("Demo login failed:", error);
      toast.error("Could not start demo session");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult();
        console.log("App: User authenticated:", user.uid);
        console.log("App: User custom claims:", idTokenResult.claims);
        const adminStatus = idTokenResult.claims.isAdmin === true;
        setUser(user);
        setIsAdmin(adminStatus);
        console.log("App: isAdmin set to:", adminStatus);
        setShowLogin(false); // Reset login modal on login
      } else {
        setUser(null);
        setIsAdmin(false);
        setShowLogin(false); // Reset on logout
      }
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const weatherApiBase = "https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/weather";
    console.log("Attempting to fetch current weather data from Weather Underground...");
    fetch(`${weatherApiBase}/current?stationId=KNCTOPTO33`)
      .then((res) => {
        console.log("Current Weather API response status:", res.status);
        if (!res.ok) throw new Error(`Current Weather API request failed with status ${res.status}`);
        return res.json();
      })
      .then((data) => {
        console.log("Current Weather API response data:", data);
        if (data.observations && data.observations.length > 0) {
          const observation = data.observations[0];
          setWeatherData({
            temp: `${observation.imperial.temp}°F`,
            humidity: `${observation.humidity}%`,
            wind: `${observation.imperial.windSpeed} mph`,
            rawData: observation
          });
        } else {
          console.log("No observations in current weather response, setting default weather data");
          setWeatherData({ temp: "N/A", humidity: "N/A", wind: "N/A" });
        }
      })
      .catch((err) => {
        console.error("Current Weather API error:", err);
        setWeatherData({ temp: "Error", humidity: "Error", wind: "Error" });
      });
    console.log("Attempting to fetch historical weather data from Weather Underground...");
    fetch(`${weatherApiBase}/hourly?stationId=KNCTOPTO33`)
      .then((res) => {
        console.log("Historical Weather API response status:", res.status);
        if (!res.ok) throw new Error(`Historical Weather API request failed with status ${res.status}`);
        return res.json();
      })
      .then((data) => {
        console.log("Historical Weather API response data:", data);
        if (data.observations && data.observations.length > 0) {
          const sortedData = data.observations.sort((a, b) => new Date(b.obsTimeUtc) - new Date(a.obsTimeUtc));
          console.log("Sorted historical data (newest first):", sortedData);
          const latestTime = new Date(sortedData[0].obsTimeUtc);
          const twentyFourHoursAgo = new Date(latestTime.getTime() - 24 * 60 * 60 * 1000);
          const filteredData = sortedData
            .filter(obs => {
              const obsTime = new Date(obs.obsTimeUtc);
              const isWithin24Hours = obsTime >= twentyFourHoursAgo;
              return isWithin24Hours;
            })
            .sort((a, b) => new Date(a.obsTimeUtc) - new Date(b.obsTimeUtc))
            .map(obs => {
              // Normalize imperial fields for consistent charting (handle missing pressure etc.)
              if (obs.imperial) {
                obs.imperial.pressure = obs.imperial.pressure ?? obs.imperial.pressureAvg ?? null;
              }
              return obs;
            });
          console.log("Filtered 24-hour historical data (first item sample):", filteredData[0]);

          if (filteredData.length > 0) {
            const sample = filteredData[0];
            const imperialKeys = sample.imperial ? Object.keys(sample.imperial) : [];
            console.log("Available keys in imperial object (historical):", imperialKeys);
            console.log("Full imperial sample from first historical obs:", sample.imperial);
          }

          const pressureSamples = filteredData.slice(-5).map(o => o.imperial?.pressure);
          console.log("Pressure sample values (last 5):", pressureSamples);
          console.log("Any non-null pressure in last 24h?", filteredData.some(o => o.imperial?.pressure != null));

          setHistoricalWeatherData(filteredData);
        } else {
          console.log("No observations in historical weather response");
          setHistoricalWeatherData([]);
        }
      })
      .catch((err) => {
        console.error("Historical Weather API error:", err);
        setHistoricalWeatherData([]);
      });
    const fetchEvents = async () => {
      if (!user) {
        console.log("Skipping fetchEvents: User not authenticated yet.");
        return;
      }
      try {
        console.log("Fetching events from Firestore...");
        const snapshot = await getDocs(collection(db, "events"));
        console.log("Events snapshot size:", snapshot.size);
        const eventList = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            date: data.date,
            start: new Date(data.date),
            end: new Date(data.date),
            allDay: true
          };
        });
        console.log("Events list:", eventList);
        setEvents(eventList);
        setEventsError(null);
      } catch (err) {
        console.error("Error fetching events:", err);
        setEventsError("Failed to load events: " + err.message);
        setEvents([]);
      }
    };
    if (user) {
      fetchEvents();
    }
  }, [user]);
  const handleLoginClick = useCallback(() => {
    console.log("Login button clicked - setting showLogin to true");
    setShowLogin(true);
  }, []);
  const handleLoginClose = useCallback(() => {
    console.log("Closing login modal - setting showLogin to false");
    setShowLogin(false);
    setPostLoginPath(null);
  }, []);
  const ringLinkPending = Boolean(postLoginPath?.includes("ring=link"));
  const handleLogout = () => {
    console.log("Logging out user");
    signOut(auth);
  };
  return (
    <ErrorBoundary>
      <Suspense fallback={<div>Loading...</div>}>
        <Router>
          <RingLinkLoginHelper onOpenLogin={handleLoginClick} setPostLoginPath={setPostLoginPath} />
          {showLogin && (
            <LoginWindow
              onClose={handleLoginClose}
              isMobile={isMobile}
              redirectTo={postLoginPath}
              ringLinkPending={ringLinkPending}
              demoMode={isDemoBuild}
            />
          )}
          <div className="app-container" style={{ backgroundImage: `url(${backgroundImage})` }}>
            <Header
              onLoginClick={handleLoginClick}
              isPublic={!user}
              user={user}
              isAdmin={isAdmin}
              onLogout={handleLogout}
              isMobile={isMobile}
            />
            <DemoBanner />
            <ToastContainer position="top-center" theme="colored" />
            <WeatherAlertBanner user={user} />
            <NannyAssistant user={user} isAdmin={isAdmin} />
            <main className="flex-grow">
              <Routes>
                <Route
                  path="/"
                  element={
                    user ? (
                      <Navigate to="/dashboard" replace />
                    ) : isDemoBuild ? (
                      <MarketingLandingPage
                        isMobile={isMobile}
                        onTryDemo={handleTryDemo}
                        onOwnerLogin={handleLoginClick}
                      />
                    ) : (
                      <MainPage
                        onLoginClick={handleLoginClick}
                        isPublic={true}
                        ringLinkPending={ringLinkPending}
                        user={user}
                        isAdmin={isAdmin}
                        weatherData={weatherData}
                        historicalWeatherData={historicalWeatherData}
                        events={events}
                        eventsError={eventsError}
                        isMobile={isMobile}
                      />
                    )
                  }
                />
                <Route
                  path="/product"
                  element={
                    <MarketingLandingPage
                      isMobile={isMobile}
                      onTryDemo={
                        isDemoBuild
                          ? handleTryDemo
                          : () => window.open(DEMO_SITE_URL, "_blank", "noopener")
                      }
                      onOwnerLogin={handleLoginClick}
                    />
                  }
                />
                <Route
                  path="/for-communities"
                  element={
                    <HoaSalesPage
                      isMobile={isMobile}
                      onTryDemo={
                        isDemoBuild
                          ? handleTryDemo
                          : () => window.open(DEMO_SITE_URL, "_blank", "noopener")
                      }
                      onOwnerLogin={handleLoginClick}
                    />
                  }
                />
                <Route
                  path="/for-communities/one-pager"
                  element={<HoaBoardOnePager isMobile={isMobile} />}
                />
                <Route
                  path="/rentals"
                  element={<ProtectedRoute isMobile={isMobile}><RentalsPage user={user} isAdmin={isAdmin} isMobile={isMobile} /></ProtectedRoute>}
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MainPage
                        isPublic={false}
                        user={user}
                        isAdmin={isAdmin}
                        weatherData={weatherData}
                        historicalWeatherData={historicalWeatherData}
                        events={events}
                        eventsError={eventsError}
                        isMobile={isMobile}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community-pics"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <CommunityPicsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community-pics/:id"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MediaDetailPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <InvoicesPage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/owners"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <OwnersPage isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contractors"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <ContractorsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <CalendarPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/board-members"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <BoardMembersPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/meeting-minutes"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MeetingMinutesPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hoa-docs"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <HOADocsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/shared-files"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <SharedFilesPage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-invoices"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <AdminInvoicesPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-finance"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <AdminFinancePage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-compliance"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <AdminCompliancePage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/compliance-calendar"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <ComplianceCalendarPage user={user} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-users"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <AdminUsersPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <HelpPage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <AdminPortalPage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/developer-guide"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <DeveloperGuidePage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/emergency-message"
                  element={
                    <ProtectedRoute isMobile={isMobile} requiresAdmin>
                      <EmergencyMessageDialogWrapper user={user} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/message-board"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MessageBoardPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/video"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <VideoPage user={user} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/preferences"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <UserPreferencesPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/privacy" element={<PrivacyPolicyPage isMobile={isMobile} />} />
                <Route path="/terms" element={<TermsAndConditionsPage isMobile={isMobile} />} />
                <Route path="/neighborhood-map" element={<NeighborhoodMap isMobile={isMobile} user={user} isAdmin={isAdmin} />} />
                <Route path="/flight-radar" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <FlightRadarPage isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/plant-tree-identification" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <PlantTreeIdentification user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/animal-identification" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <AnimalIdentification user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/rock-mineral-identification" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <MineralIdentification user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/star-map" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <Suspense fallback={<div>Loading Star Map...</div>}>
                      <StarMapPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/local-river-info" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <LocalRiverInfo user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/seismic-monitoring" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <SeismicMonitoring user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/road-conditions" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <RoadConditionsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/well-monitoring" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <WellMonitoringPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/gate-access" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <GateAccessPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/mailbox" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <MailboxPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/vehicle-log" element={
                  <ProtectedRoute isMobile={isMobile} requiresAdmin>
                    <VehicleLogPage user={user} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/violations" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <ViolationsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/arc-review" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <ArcReviewPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/community-services" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <CommunityServicesPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/marketplace" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <MarketplacePage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/environmental" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <EnvironmentalPage isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/admin-sms" element={
                  <ProtectedRoute isMobile={isMobile} requiresAdmin>
                    <AdminSmsPage isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/admin-mailbox" element={
                  <ProtectedRoute isMobile={isMobile} requiresAdmin>
                    <AdminMailboxPage user={user} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/polls" element={
                  <ProtectedRoute isMobile={isMobile}>
                    <PollsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
                <Route path="/admin-polls" element={
                  <ProtectedRoute isMobile={isMobile} requiresAdmin>
                    <AdminPollsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                  </ProtectedRoute>
                } />
              </Routes>
            </main>
          </div>
        </Router>
      </Suspense>
    </ErrorBoundary>
  );
}
export default App;
export { db };